import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';

import {
  IArnoDecisionTemplate,
  useSetArnoTemplateAssignments,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import { useGetLegalTransactionTypes } from '../../hooks/data';
import {
  lifecycleChip,
  selectionSummary,
  templateSourceChip,
} from './arnoAdminLabels';
import {
  assignmentsPayload,
  initialSelection,
  Selection,
  selectionChanges,
  toggleDefault,
  toggleSelection,
  visibleTypes,
} from './templateSelection';
import { useAlert } from '../../context/AlertContext';

/**
 * Freigabe eines Entscheidungsbaums für DiNo-Rechtsgeschäftsarten (#1245).
 *
 * Ein Baum gilt für beliebig viele Rechtsgeschäftsarten, und produktiv gibt es
 * hunderte davon. Deshalb eine Liste mit Suche und einem Filter auf das bereits
 * Ausgewählte statt hunderter Kästchen untereinander, und ein einziges Speichern
 * für die ganze Menge.
 *
 * Der Stern markiert die Vorauswahl bei der Urkundenerzeugung. Je
 * Rechtsgeschäftsart kann nur ein Baum die Vorgabe sein; das Backend löst eine
 * bestehende Vorgabe ab, und der Hinweis hier sagt das vorher.
 */

interface Props {
  template: IArnoDecisionTemplate | null;
  onClose: () => void;
}

export const TemplateAssignmentDialog: React.FC<Props> = ({
  template,
  onClose,
}) => {
  const { showAlert } = useAlert();
  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();
  const { setTemplateAssignments, isSettingTemplateAssignments } =
    useSetArnoTemplateAssignments();

  const [search, setSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const [selection, setSelection] = useState<Selection>({});

  // Beim Öffnen den gespeicherten Stand übernehmen.
  useEffect(() => {
    setSelection(initialSelection(template));
    setSearch('');
    setOnlySelected(false);
  }, [template]);

  const types = useMemo(
    () => legalTransactionTypes ?? [],
    [legalTransactionTypes]
  );

  const visible = useMemo(
    () => visibleTypes(types, selection, search, onlySelected),
    [types, search, onlySelected, selection]
  );

  const selectedCount = Object.keys(selection).length;
  const changes = selectionChanges(template?.assignments ?? [], selection);
  const hasChanges = changes.added + changes.removed + changes.changed > 0;

  const save = async () => {
    if (!template) {
      return;
    }
    try {
      await setTemplateAssignments({
        idDecisionTemplate: template.idDecisionTemplate,
        assignments: assignmentsPayload(selection),
      });
      showAlert({
        severity: 'success',
        text: `${template.name} ist für ${selectedCount} Rechtsgeschäftsart${
          selectedCount === 1 ? '' : 'en'
        } freigegeben.`,
      });
      onClose();
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Speichern fehlgeschlagen: ${error.message}`
            : 'Speichern fehlgeschlagen.',
      });
    }
  };

  const source = template ? templateSourceChip(template.sourceType) : null;
  const lifecycle = template ? lifecycleChip(template.lifecycleStatus) : null;

  return (
    <Dialog
      open={Boolean(template)}
      onClose={isSettingTemplateAssignments ? undefined : onClose}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='h6' sx={{ flexGrow: 1 }}>
            {template?.name}
          </Typography>
          {source && (
            <Chip size='small' color={source.color} label={source.label} />
          )}
          {lifecycle && (
            <Chip
              size='small'
              color={lifecycle.color}
              label={lifecycle.label}
            />
          )}
        </Stack>
        <Typography variant='caption' color='text.secondary'>
          Für welche DiNo-Rechtsgeschäftsarten dieser Entscheidungsbaum gilt
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {template?.lifecycleStatus === 'DRAFT' && (
          <Alert severity='info' sx={{ mb: 2 }}>
            Solange der Baum ein Entwurf ist, wird er bei einem Vorgang nicht
            angeboten — die Freigaben lassen sich trotzdem schon vorbereiten.
          </Alert>
        )}

        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
        >
          <TextField
            size='small'
            label='Rechtsgeschäftsart suchen'
            value={search}
            onChange={event => setSearch(event.target.value)}
            sx={{ minWidth: 280 }}
          />
          <TextField
            size='small'
            select
            label='Anzeige'
            value={onlySelected ? 'selected' : 'all'}
            onChange={event =>
              setOnlySelected(event.target.value === 'selected')
            }
            sx={{ minWidth: 200 }}
          >
            <MenuItem value='all'>Alle</MenuItem>
            <MenuItem value='selected'>Nur ausgewählte</MenuItem>
          </TextField>
          <Typography variant='body2' color='text.secondary'>
            {selectionSummary(selectedCount, types.length)}
            {hasChanges
              ? ` · ${changes.added} neu, ${changes.removed} entfernt, ${changes.changed} geändert`
              : ''}
          </Typography>
        </Stack>

        <Divider />

        {isLoadingLegalTransactionTypes ? (
          <Stack alignItems='center' sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        ) : visible.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              {onlySelected
                ? 'Noch keine Rechtsgeschäftsart ausgewählt.'
                : 'Keine Rechtsgeschäftsart passt zur Suche.'}
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 420, overflow: 'auto' }}>
            {visible.map(type => {
              const entry = selection[type.idLegalTransactionType];
              return (
                <ListItemButton
                  key={type.idLegalTransactionType}
                  onClick={() =>
                    setSelection(current =>
                      toggleSelection(current, type.idLegalTransactionType)
                    )
                  }
                  disabled={isSettingTemplateAssignments}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      edge='start'
                      checked={Boolean(entry)}
                      tabIndex={-1}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={type.LegalTransactionType}
                    secondary={
                      entry && !entry.isEnabled ? 'ausgesetzt' : undefined
                    }
                  />
                  <Tooltip
                    title={
                      entry
                        ? 'Als Vorauswahl bei der Urkundenerzeugung setzen. Eine bestehende Vorgabe dieser Rechtsgeschäftsart wird dabei abgelöst.'
                        : 'Erst auswählen, dann kann der Baum die Vorgabe sein.'
                    }
                  >
                    <span>
                      <IconButton
                        size='small'
                        disabled={!entry || isSettingTemplateAssignments}
                        onClick={event => {
                          event.stopPropagation();
                          setSelection(current =>
                            toggleDefault(current, type.idLegalTransactionType)
                          );
                        }}
                      >
                        {entry?.isDefault ? (
                          <Star fontSize='small' color='warning' />
                        ) : (
                          <StarBorder fontSize='small' />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </ListItemButton>
              );
            })}
          </List>
        )}

        {(template?.assignmentsMissing ?? 0) > 0 && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {template?.assignmentsMissing} Freigabe(n) zeigen auf eine
            Rechtsgeschäftsart, die es nicht mehr gibt. Beim Speichern fallen
            sie weg.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSettingTemplateAssignments}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          onClick={save}
          disabled={isSettingTemplateAssignments || !hasChanges}
          startIcon={
            isSettingTemplateAssignments ? (
              <CircularProgress size={16} />
            ) : undefined
          }
        >
          Freigaben speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};
